<script lang="ts" setup>
import {
  SizeTypeLabel,
  TechDataVariations,
  TechDataVehicle,
  Translations,
  TransmissionMode,
} from '@/types/types';
import { useVehicleSelection } from '@/composables/useVehicleSelection';
import { getVehicleType } from '@/utils/vehicleType';
import { useTranslations } from '@/composables/useTranslations';
import { useTracking } from '@/composables/useTracking';
import { computed, watch, toRef, onMounted } from 'vue';
import TechDataTagGroup from './tech-data-tag-group.vue';
import WbIcon from '../../../wb/Icon/WbIcon.vue';
import {
  normalizeTransmission,
  hasMultipleTransmissions,
} from '@/utils/normalize';

const TRANSMISSIONOPTIONS: TransmissionMode[] = ['manual', 'automatic'];
const props = defineProps<{
  sizes: SizeTypeLabel[];
  validOptions: {
    weights: number[] | null;
    roofs: string[] | null;
    cabins: string[] | null;
    drivetrains: string[] | null;
    batteryCapacities: number[] | null;
    engines: string[] | null;
  };
  variations: TechDataVariations;
  vehicles: TechDataVehicle[];
  translations: Translations;
  modelSeries: string;
  selectedVariant?: TechDataVehicle;
}>();

const emit = defineEmits(['variant-selected', 'selected-transmission']);

const vehicleType: 'Citan' | 'Vito' | 'Sprinter_Cabin' | 'Sprinter_Roof' =
  getVehicleType(props.variations) ?? 'Sprinter_Cabin';

const {
  vehicle,
  available_weights,
  available_cabins,
  available_roofs,
  available_engines,
  available_batteryCapacities,
  available_drivetrains,
  available_transmissions,
  selectCabin,
  selectRoof,
  selectEngine,
  selectBatteryCapacity,
  selectWeight,
  selectDrivetrain,
  selectTransmission,
  setVehiclePropertiesBySize,
  selectedTransmission,
} = useVehicleSelection(
  vehicleType,
  props.variations,
  props.vehicles,
  props.selectedVariant,
);

const { trackFeatureSelection } = useTracking(props.modelSeries);
const {
  getSizeTypeTranslation,
  getRoofTypeTranslation,
  getCabinTypeTranslation,
  getDrivetrainTranslation,
  getTransmissionTranslation,
  getEngineTypeTranslation,
  getComponentLabel,
} = useTranslations(toRef(props, 'translations'));

const selectedVehicle = computed(() => vehicle.value);
const selectedSize = computed(() => selectedVariation.value.sizeType);

function isSizeSelected(item: { sizeType: string }) {
  return item.sizeType === selectedSize.value;
}

const selectedVariation = computed(() => {
  // Find the variation that matches the current vehicle (or fallback to first variation)
  return (
    props.variations.variations.find(
      (v: any) => v.sizeType === vehicle.value?.sizeType,
    ) || props.variations.variations[0]
  );
});
function onSelectSize(variationItem: any) {
  setVehiclePropertiesBySize(variationItem);
  trackFeatureSelection(
    `size_type.${variationItem.sizeType}`,
    vehicle.value ?? null,
  );
}

onMounted(() => {
  emit('variant-selected', selectedVehicle.value);
  emit('selected-transmission', selectedTransmission.value);
});

watch(
  [selectedVehicle, selectedTransmission],
  ([newVehicle, transmission], [oldVehicle, oldTransmission]) => {
    if (newVehicle === oldVehicle && transmission === oldTransmission) return;
    emit('variant-selected', newVehicle);
    emit('selected-transmission', transmission);
  },
  { immediate: false },
);

function transmissionToWeightKey(
  t: TransmissionMode | null | undefined,
): TransmissionMode | null {
  if (!t) return null;

  const v = String(t).toLowerCase();
  return v === 'manual' || v === 'automatic' ? (v as TransmissionMode) : null;
}

const selectedWeightValue = computed(() => {
  const key = transmissionToWeightKey(selectedTransmission.value ?? null);
  const v = vehicle.value;

  return key ? (v?.weights?.[key]?.weightMax?.value ?? null) : null;
});

const showTransmissionFilter = computed(() =>
  hasMultipleTransmissions(props.vehicles),
);
</script>

<template>
  <div class="owvans-tech-data-selector wb-grid-container">
    <div v-if="variations.variations.length > 1">
      <fieldset class="owvans-tech-data-selector__fieldset">
        <legend class="owvans-tech-data-selector__legend wb-text-s">
          {{
            getComponentLabel('owvc_tech_data_sizeTypeLabel', 'sizeTypeLabel')
          }}
        </legend>
        <div class="owvans-tech-data-selector__options">
          <label
            v-for="(variationItem, index) in variations.variations"
            :key="`size-tag${index}`"
            class="wbx-tag wb-text-s-strong wbx-tag--tertiary owvans-tech-data-selector__option"
            :class="{
              'owvans-tech-data-selector__option--default':
                !isSizeSelected(variationItem),
              'owvans-tech-data-selector__option--active':
                isSizeSelected(variationItem),
            }"
          >
            <input
              class="owvans-tech-data-selector__input"
              type="radio"
              name="sizeType"
              :value="variationItem.sizeType"
              :checked="
                isSizeSelected(variationItem) || (!selectedSize && index === 0)
              "
              :tabindex="
                isSizeSelected(variationItem) || (!selectedSize && index === 0)
                  ? 0
                  : -1
              "
              @change="onSelectSize(variationItem)"
            />
            <span class="wb-text wb-text-m-strong-static">
              {{
                getSizeTypeTranslation(
                  variationItem.sizeType as unknown as
                    | 'COMPACT'
                    | 'STANDARD'
                    | 'LONG'
                    | 'EXTRA_LONG',
                )
              }}
            </span>
            <WbIcon
              :name="
                isSizeSelected(variationItem)
                  ? 'bds/hotspot-active/24'
                  : 'bds/hotspot-default/24'
              "
            />
          </label>
        </div>
      </fieldset>
    </div>

    <TechDataTagGroup
      name="roofs"
      :label="
        getComponentLabel('owvc_tech_data_roofTypeLabel', 'roofTypeLabel')
      "
      v-if="validOptions.roofs"
      :options="validOptions.roofs"
      :selected="vehicle?.roofHeightType"
      :available="available_roofs"
      :getOptionLabel="getRoofTypeTranslation"
      :onSelect="selectRoof"
      :onTrack="trackFeatureSelection"
      trackPrefix="roof_type"
      :vehicle="vehicle"
    />

    <TechDataTagGroup
      name="cabins"
      :label="
        getComponentLabel('owvc_tech_data_cabinTypeLabel', 'cabinTypeLabel')
      "
      v-if="validOptions.cabins"
      :options="validOptions.cabins"
      :selected="vehicle?.cabinType"
      :available="available_cabins"
      :getOptionLabel="getCabinTypeTranslation"
      :onSelect="selectCabin"
      :onTrack="trackFeatureSelection"
      trackPrefix="cabin_type"
      :vehicle="vehicle"
    />

    <TechDataTagGroup
      name="engines"
      :label="getComponentLabel('owvc_tech_data_engine', 'engine')"
      v-if="validOptions.engines"
      :options="validOptions.engines"
      :selected="vehicle?.engineType"
      :available="available_engines"
      :getOptionLabel="getEngineTypeTranslation"
      :onSelect="selectEngine"
      :onTrack="trackFeatureSelection"
      trackPrefix="engine_type"
      :vehicle="vehicle"
    />

    <TechDataTagGroup
      name="batteryCapacity"
      :label="
        getComponentLabel('owvc_tech_data_batteryCapacity', 'batteryCapacity')
      "
      v-if="validOptions.batteryCapacities"
      :options="validOptions.batteryCapacities"
      :selected="vehicle?.batteryCapacity"
      :available="available_batteryCapacities"
      :getOptionLabel="(v) => v"
      :onSelect="selectBatteryCapacity"
      :onTrack="trackFeatureSelection"
      trackPrefix="battery_capacity"
      :vehicle="vehicle"
    />

    <TechDataTagGroup
      name="weights"
      :label="getComponentLabel('owvc_tech_data_weight', 'weight')"
      v-if="validOptions.weights"
      :options="validOptions.weights"
      :selected="selectedWeightValue"
      :available="available_weights"
      :getOptionLabel="(v) => v"
      :onSelect="selectWeight"
      :onTrack="trackFeatureSelection"
      trackPrefix="weight"
      :vehicle="vehicle"
    />

    <TechDataTagGroup
      name="drivetrains"
      :label="
        getComponentLabel('owvc_tech_data_drivetrainType', 'drivetrainType')
      "
      v-if="validOptions.drivetrains"
      :options="validOptions.drivetrains"
      :selected="vehicle?.drivetrainType"
      :available="available_drivetrains"
      :getOptionLabel="getDrivetrainTranslation"
      :onSelect="selectDrivetrain"
      :onTrack="trackFeatureSelection"
      trackPrefix="drivetrain"
      :vehicle="vehicle"
    />

    <TechDataTagGroup
      name="transmissions"
      :label="getComponentLabel('owvc_tech_data_transmission', 'transmission')"
      v-if="showTransmissionFilter"
      :options="TRANSMISSIONOPTIONS"
      :selected="selectedTransmission"
      :available="available_transmissions"
      :getOptionLabel="getTransmissionTranslation"
      :onSelect="selectTransmission"
      :onTrack="trackFeatureSelection"
      trackPrefix="transmission"
      :vehicle="vehicle"
    />
  </div>
</template>

<style lang="scss" scoped>
@import '@workbench/core/dist/scss/utility.scss';
@import '@workbench/core/dist/css/debuggers/media-query.css';
.owvans-tech-data-selector {
  display: flex;
  flex-wrap: wrap;
  gap: 16px 64px;
  margin-top: 32px;
  margin-bottom: 32px;

  &__fieldset {
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin: 0;
    padding: 0;
    border: 0;
    min-inline-size: 0;
  }

  &__legend {
    color: var(--wb-grey-20);
    display: -ms-flexbox;
    font-family: var(--wb-font-text);
    font-size: 1rem;
    font-style: normal;
    font-weight: 700;
    margin-bottom: 8px;
  }

  &__options {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    min-inline-size: 0;
  }

  &__option {
    display: flex;
    flex: 0 0 auto;
    position: relative;
    align-items: center;
    gap: var(--wb-interactive-tag-md-gap-right);
    border: none;
    border-radius: var(--wb-interactive-tag-border-radius);
    padding: 4px 11px;
    cursor: pointer;
  }

  &__option--active {
    &:hover {
      background-color: #d5d5d5;
    }
  }

  &__option--default {
    background-color: transparent;
    border: 1px solid var(--wb-grey-20);

    &:hover {
      box-shadow: 0 0 0 1px
        var(--wb-interactive-tag-tertiary-unfilled-border-main-color-hover);
    }
  }

  &__input {
    position: absolute;
    inset: 0;
    opacity: 0;
    margin: 0;
    width: 100%;
    height: 100%;
    cursor: inherit;
  }

  &__option:has(.owvans-tech-data-selector__input:focus-visible) {
    outline: 1px solid var(--wb-color-border-brand-active);
    outline-offset: 2px;
    border-radius: var(--wb-interactive-tag-border-radius);
  }
}
</style>
